import {Avatar, AvatarFallback} from "../../basic/avatar.tsx";
import {Badge} from "../../basic/badge.tsx";
import {useTranslationWithKeys} from "../../../hooks/useTranslationWithKeys";
import {ColumnDef, DataTable} from "./DataTable.tsx";
import {VwpArchivedMember} from "../../../store/generated/api.ts";
import {getInitials} from "../../../utils/nameUtils.ts";
import {formatDate} from "../../../utils/dateUtils.ts";

interface ArchivedMemberTableProps {
    paginatedData: VwpArchivedMember[];
    showVisibility?: boolean;
}

export function ArchivedMemberTable({paginatedData, showVisibility = false}: ArchivedMemberTableProps) {
    const {t} = useTranslationWithKeys();

    const getMemberName = (member: VwpArchivedMember): { name: string; firstName?: string; lastName?: string } => {
        if (member.snapshot && typeof member.snapshot === 'object') {
            const snapshot = member.snapshot as any;

            const user = snapshot.user;
            if (user && typeof user === 'object') {
                const firstName = user.firstName?.trim() || '';
                const lastName = user.lastName?.trim() || '';

                if (firstName || lastName) {
                    const fullName = `${firstName} ${lastName}`.trim();
                    return {
                        name: fullName,
                        firstName: firstName || undefined,
                        lastName: lastName || undefined
                    };
                }
            }

            const firstName = snapshot.firstName?.trim() || '';
            const lastName = snapshot.lastName?.trim() || '';

            if (firstName || lastName) {
                const fullName = `${firstName} ${lastName}`.trim();
                return {
                    name: fullName,
                    firstName: firstName || undefined,
                    lastName: lastName || undefined
                };
            }
        }
        return {name: member.userEmail};
    };

    const columns: ColumnDef<VwpArchivedMember>[] = [
        {
            key: "id",
            header: t('common.table.name'),
            cell: (member) => {
                const {name, firstName, lastName} = getMemberName(member);
                return (
                    <div className="flex items-center gap-3">
                        <Avatar>
                            <AvatarFallback>{getInitials({
                                email: member.userEmail,
                                firstName,
                                lastName
                            })}</AvatarFallback>
                        </Avatar>
                        <p className="font-medium text-sm">{name}</p>
                    </div>
                );
            },
        },
        {
            key: "userEmail",
            header: t('common.table.email'),
            cell: (member) => (
                <p className="text-sm text-muted-foreground">{member.userEmail}</p>
            ),
        },
        {
            key: "removedAt",
            header: t('team.removedAt'),
            cell: (member) => (
                <p className="text-sm text-muted-foreground">
                    {member.removedAt ? formatDate(new Date(member.removedAt)) : '-'}
                </p>
            ),
        },
    ];

    if (showVisibility) {
        columns.push({
            key: "visibility",
            header: t('common.table.visibility'),
            cell: (member) => (
                member.visibility === false
                    ? <Badge variant="secondary">{t('common.hidden')}</Badge>
                    : <Badge variant="outline">{t('common.visible')}</Badge>
            ),
        });
    }

    return <DataTable columns={columns} data={paginatedData}/>;
}
